/**
 * Markdown 渲染管线。
 *
 * 两条安全红线写在这里，而不是散落在组件里：
 * 1. markdown-it 关闭 `html`：原文里的 `<script>` 会被**转义**成文本，不会被解析成标签。
 * 2. 渲染结果再过一遍 `sanitizeRenderedHtml`：即使将来有人打开了 `html`，或者
 *    markdown 语法本身产出了危险链接（`javascript:`），也会被剥掉。
 *
 * 全应用只有 `StreamingMarkdown.vue` 一处使用 `v-html`，注入的内容必须且只能
 * 是本文件 `renderMarkdown()` 的返回值。
 */

import MarkdownIt from 'markdown-it';

/**
 * `breaks: true` 是刻意的：聊天里用户按一次回车就是想要换行，
 * 而不是遵循 CommonMark 的"软换行折叠成空格"。
 * `typographer: false`：中文排版不需要它，开了反而会把引号替换成英文弯引号。
 */
const md: MarkdownIt = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
  typographer: false,
});

const defaultLinkOpen =
  md.renderer.rules['link_open'] ??
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules['link_open'] = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  if (token !== undefined) {
    token.attrSet('target', '_blank');
    // `noopener` 防 window.opener 劫持；`noreferrer` 保护隐私；`nofollow` 因为我们
    // 无法为外部内容的可信度背书——AI 生成的链接可能指向任何地方。
    token.attrSet('rel', 'noopener noreferrer nofollow');
  }
  return defaultLinkOpen(tokens, idx, options, env, self);
};

/** 成对出现的块级标签，整段删除（含内部内容）。 */
const SCRIPT_LIKE_BLOCK =
  /<\s*(script|iframe|object|embed|style|link|meta|base|form|svg|math)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi;
/** 自闭合或未闭合的危险标签。 */
const SCRIPT_LIKE_VOID = /<\s*(script|iframe|object|embed|link|meta|base|form)\b[^>]*\/?>/gi;
/** 内联事件处理器：`onclick="..."` / `onerror=alert(1)`。 */
const INLINE_EVENT_HANDLER = /\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;
/** 危险协议：`href="javascript:..."`、`src='data:text/html...'`。 */
const DANGEROUS_URL_ATTR =
  /\s(href|src|xlink:href)\s*=\s*("|')?\s*(javascript|vbscript|data:text\/html)[^"'\s>]*/gi;

/** 扫描未闭合的代码围栏，返回围栏字符（`` ` `` 或 `~`），没有则返回 null。 */
function findUnclosedFence(source: string): string | null {
  let open: string | null = null;
  for (const line of source.split('\n')) {
    const match = /^ {0,3}(`{3,}|~{3,})/.exec(line);
    if (match === null) {
      continue;
    }
    const fence = match[1]?.[0] ?? null;
    if (fence === null) {
      continue;
    }
    if (open === null) {
      open = fence;
    } else if (open === fence) {
      open = null;
    }
  }
  return open;
}

/**
 * 流式补丁：给未闭合的代码围栏补上收尾。
 *
 * 不这么做的话，每个 delta 都会让整块内容在"代码块内/外"之间反复翻转——
 * 用户会看到排版每 40ms 抖一次。补的只是**渲染用的副本**，绝不写回状态。
 */
export function stabilizeStreamingMarkdown(source: string): string {
  const unclosed = findUnclosedFence(source);
  if (unclosed === null) {
    return source;
  }
  return `${source}\n${unclosed.repeat(3)}`;
}

/**
 * 输出侧净化。
 *
 * 这是纵深防御的第二层：markdown-it 已经把原文 HTML 转义了，但
 * "转义"依赖配置正确，而这里对**输出**做无条件的模式剔除，
 * 即使上游配置被改错也不会漏出可执行内容。
 */
export function sanitizeRenderedHtml(html: string): string {
  return html
    .replace(SCRIPT_LIKE_BLOCK, '')
    .replace(SCRIPT_LIKE_VOID, '')
    .replace(INLINE_EVENT_HANDLER, '')
    .replace(DANGEROUS_URL_ATTR, '');
}

export interface RenderedMarkdown {
  html: string;
  /** 流式渲染时是否补过围栏（调用方可用于 debug，不影响展示）。 */
  streamingUnbalanced: boolean;
}

/** 渲染 Markdown。`streaming: true` 时启用围栏补全。 */
export function renderMarkdown(source: string, options: { streaming?: boolean } = {}): RenderedMarkdown {
  const unbalanced = options.streaming === true && findUnclosedFence(source) !== null;
  const prepared = unbalanced ? stabilizeStreamingMarkdown(source) : source;
  return { html: sanitizeRenderedHtml(md.render(prepared)), streamingUnbalanced: unbalanced };
}

/** Markdown → 纯文本，供列表摘要、标题推导、`aria-label` 使用。 */
export function markdownToPlainText(source: string): string {
  return source
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^ {0,3}#{1,6}\s+/gm, '')
    .replace(/^ {0,3}>\s?/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * 从首条消息推导会话标题。
 *
 * 按**码点**截断而不是 `slice`：`Array.from` 会把代理对当成一个字符，
 * 直接从中间切会留下半个 emoji（渲染成 `` ）。
 */
export function deriveTitle(source: string, maxLength: number): string {
  const text = markdownToPlainText(source).replace(/\s+/g, ' ').trim();
  if (text.length === 0) {
    return '新会话';
  }
  const codePoints = Array.from(text);
  if (codePoints.length <= maxLength) {
    return text;
  }
  return `${codePoints.slice(0, maxLength).join('')}…`;
}
